import type { AwardLetters, LetterGap } from '@myrewards/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { saveLetterFallback } from '../../api/letters';
import {
  Button,
  ButtonLink,
  Card,
  ConfirmDialog,
  EmptyState,
  formatShortDate,
  Icon,
  plural,
  Select,
  TextField,
} from '../../ui';
import { TYPE_LABEL } from '../labels';
import { Notice } from '../shared';
import {
  candidateLabel,
  gapNames,
  gapPeopleWords,
  gapTitle,
  letterProblemWords,
  MIX_WORDS,
  signedByWords,
} from './presenter';
import { letterCellHref } from '../../settings/letters/links';
import l from './Letters.module.css';
import o from '../Ops.module.css';

/**
 * APP-130: one gap on this award, a signer and mix no letter resolves for. Two fixes: upload the
 * missing letter into its cell in Settings › Elections and letters, or use another approved letter of the same
 * type and mix, any signer, with a reason (D8). The fallback is saved for everyone in the gap and
 * used until the missing letter is ready (D7, D9); it makes no letter by itself. The board's gold
 * accent and its title, whose letter is waiting on a template (D104).
 */
export function GapCard({
  letters,
  gap,
  onSaved,
}: {
  letters: AwardLetters;
  gap: LetterGap;
  onSaved: (next: AwardLetters, said: string) => Promise<void>;
}): ReactElement {
  const canFix = letters.actions.fallback === true;
  const saved = gap.fallback;
  const [editing, setEditing] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [stopping, setStopping] = useState(false);
  const save = useMutation({
    mutationFn: (body: { templateId: string | null; reason: string }) =>
      saveLetterFallback(letters.requestId, { cellKey: gap.cellKey, ...body }),
  });
  const n = gap.people.length;
  const chosen = gap.candidates.find((t) => t.id === templateId);
  const formOpen = canFix && (saved === null || editing);
  // APP-212: with no stand-in, the built-in sample letter fills the gap meanwhile.
  const sample = gap.builtInSample != null;
  const help = {
    shows: `The ${TYPE_LABEL[letters.type]} letter signed by ${gap.signer.name} for ${MIX_WORDS[gap.mix].toLowerCase()} awards isn’t ready, so ${
      sample
        ? `${n === 1 ? 'this person’s letter is' : 'these letters are'} made from the built-in sample letter, marked as a sample, until it is`
        : `${n === 1 ? 'this person’s letter' : 'these letters'} can’t be made yet`
    }. Everyone else’s letters can go ahead.`,
    canDo:
      'Upload the missing letter in Settings › Elections and letters, or use another approved letter of the same type and amounts with a reason. The other letter is used only until the missing one is ready.',
  };

  return (
    <Card title={gapTitle(gap)} help={help} accent="gold">
      <dl className={l.facts}>
        <dt>Signed by</dt>
        <dd>{gap.signer.name}</dd>
        <dt>Amounts</dt>
        <dd>{MIX_WORDS[gap.mix]}</dd>
        <dt>{n === 1 ? 'Whose letter' : 'Whose letters'}</dt>
        <dd>{gapNames(gap)}</dd>
      </dl>
      {saved !== null && (
        <Notice tone={saved.usable ? 'info' : 'problem'}>
          {saved.usable
            ? `Using ${candidateLabel(saved.template)} until the missing letter is ready. ${signedByWords(saved.template, gap.signer)}. “${saved.reason}” · ${saved.by.name}, ${formatShortDate(saved.at)}.`
            : `${candidateLabel(saved.template)} was chosen to stand in, but it can’t be used any more. Upload the missing letter, or choose another.`}
        </Notice>
      )}
      <div className={l.fixes}>
        <section className={l.fix} aria-label="Upload the missing letter">
          <h3 className={l.fixTitle}>Upload the missing letter</h3>
          <p className={o.muted}>
            In Settings › Elections and letters, into {gap.signer.name}’s{' '}
            {MIX_WORDS[gap.mix].toLowerCase()} cell. Once it’s approved,{' '}
            {n === 1 ? 'this letter' : 'these letters'} can be made from it.
          </p>
          <ButtonLink
            href={letterCellHref(gap.cellKey)}
            variant="navy"
            icon={<Icon name="upload" size={16} />}
          >
            Upload the missing letter
          </ButtonLink>
        </section>
        <section className={l.fix} aria-label="Use another approved letter">
          <h3 className={l.fixTitle}>Use another approved letter</h3>
          {gap.candidates.length === 0 ? (
            <EmptyState
              variant="not-set"
              art="letter"
              size="sm"
              headline="No other approved letter fits"
              body={`There’s no approved ${MIX_WORDS[gap.mix].toLowerCase()} ${TYPE_LABEL[letters.type]} letter from another signer yet. Upload the missing letter instead.`}
            />
          ) : !canFix ? (
            <p className={o.muted}>
              A Stock Admin holding the claim can choose one of{' '}
              {plural(gap.candidates.length, 'approved letter', 'approved letters')} to stand in,
              with a reason.
            </p>
          ) : !formOpen ? (
            <div className={l.buttons}>
              <Button
                variant="ghost"
                onPress={() => {
                  setEditing(true);
                  setTemplateId(saved?.template.id ?? null);
                }}
              >
                Choose another
              </Button>
              <Button
                variant="ghost"
                onPress={() => {
                  setError(null);
                  setStopping(true);
                }}
              >
                Stop using it
              </Button>
            </div>
          ) : (
            <form
              className={o.stack}
              onSubmit={(e) => {
                e.preventDefault();
                if (templateId === null || reason.trim() === '') return;
                setError(null);
                save.mutate(
                  { templateId, reason: reason.trim() },
                  {
                    onSuccess: (next) => {
                      setEditing(false);
                      setReason('');
                      void onSaved(
                        next,
                        `${chosen === undefined ? 'The chosen letter' : candidateLabel(chosen)} stands in`,
                      );
                    },
                    onError: (err) => {
                      setError(letterProblemWords(err));
                    },
                  },
                );
              }}
            >
              <Select
                label="Approved letter to use"
                options={gap.candidates.map((t) => ({ id: t.id, label: candidateLabel(t) }))}
                value={templateId}
                onChange={setTemplateId}
                description="The same type and amounts, signed by someone else."
              />
              {chosen !== undefined && (
                <Notice tone={chosen.signer?.id === gap.signer.id ? 'info' : 'waiting'}>
                  {signedByWords(chosen, gap.signer)}.
                </Notice>
              )}
              <TextField
                label="Why use this letter?"
                description={`Saved for ${n === 1 ? 'this person' : `all ${String(n)} people`} and in the award’s history.`}
                value={reason}
                onChange={setReason}
                isRequired
                maxLength={500}
              />
              <div className={l.buttons}>
                <Button
                  type="submit"
                  variant="navy"
                  isPending={save.isPending}
                  isDisabled={templateId === null || reason.trim() === ''}
                >
                  {n === 1 ? 'Use it for this letter' : `Use it for these ${String(n)} letters`}
                </Button>
                {saved !== null && (
                  <Button
                    variant="ghost"
                    onPress={() => {
                      setEditing(false);
                      setError(null);
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          )}
        </section>
      </div>
      {error !== null && <Notice tone="problem">{error}</Notice>}
      <ConfirmDialog
        isOpen={stopping}
        onOpenChange={setStopping}
        title="Stop using the stand-in letter?"
        intro={`${n === 1 ? 'This letter is' : 'These letters are'} made from the built-in sample letter again until the missing letter is ready. Letters already made from the stand-in stay as they are until they’re made again.`}
        summary={[
          { label: 'Stand-in', value: saved === null ? '' : candidateLabel(saved.template) },
          // APP-212: what stopping leaves: the built-in sample letter fills the gap again.
          {
            label: 'Meanwhile',
            value: gapPeopleWords({ ...gap, fallback: null, builtInSample: { version: 1 } }),
          },
        ]}
        reasonLabel="Why stop using it?"
        confirmLabel="Stop using it"
        error={error}
        onConfirm={async (why) => {
          try {
            const next = await save.mutateAsync({ templateId: null, reason: why.trim() });
            await onSaved(next, 'The stand-in letter is no longer used');
            return true;
          } catch (e) {
            setError(letterProblemWords(e));
            return false;
          }
        }}
      />
    </Card>
  );
}
